import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServicePage.css';
import { setPageMeta } from '../utils/seo';

const PayrollOutsourcing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      'Smart Payroll Management Solutions | HRCONSOL',
      'AI-powered, compliant payroll management with ESS, multi-location support, analytics, and 99.9% accuracy. Streamline payroll end-to-end.'
    );
  }, []);

  return (
    <div className="service-page">
      <div className="service-hero">
        <div className="container">
          <h1>Smart Payroll Management Solutions</h1>
          <p className="subtitle">Simplify. Automate. Comply.</p>
        </div>
      </div>

      <div className="container">
        <div className="service-content">
          <div className="service-section">
            <p>HRCONSOL's Payroll Management Solutions empower organizations to manage employee compensation seamlessly — ensuring accuracy, compliance, and efficiency across all workforce levels. From blue-collar to executive teams, we handle payroll so you can focus on growing your business.</p>
          </div>

          <div className="service-section">
            <h2>Why Choose HRCONSOL Payroll Management?</h2>
            <p style={{ textAlign: 'center', fontSize: '1.1rem', lineHeight: '1.8', maxWidth: '900px', margin: '0 auto 2rem' }}>
              Managing payroll isn't just about paying salaries — it's about building trust, compliance, and operational excellence. Our AI-powered and cloud-based Payroll System ensures 100% accuracy, on-time disbursements, and real-time insights for strategic decision-making.
            </p>
          </div>

          <div className="service-section">
            <h2>Scope of Services – Premium Payroll Features</h2>
            <ul className="benefits-list">
              <li><i className="fas fa-cogs"></i> Automated Payroll Processing – Reduce manual errors with end-to-end automation</li>
              <li><i className="fas fa-balance-scale"></i> Taxation & Statutory Compliance – Stay compliant with EOBI, SESSI, gratuity, and income tax laws</li>
              <li><i className="fas fa-globe"></i> Multi-Location & Multi-Currency Support – Designed for local and international operations</li>
              <li><i className="fas fa-user-circle"></i> Employee Self-Service Portal – Empower staff with digital access to payslips, tax forms & benefits</li>
              <li><i className="fas fa-clock"></i> Attendance Integration – Sync with biometric or cloud-based attendance for real-time accuracy</li>
              <li><i className="fas fa-money-bill-wave"></i> Customized Salary Structures – Flexible configuration for different employee grades and projects</li>
              <li><i className="fas fa-shield-alt"></i> Data Security & Confidentiality – Enterprise-grade encryption for sensitive payroll data</li>
              <li><i className="fas fa-chart-bar"></i> Detailed Analytics & Reporting – Get insights on cost analysis, overtime, and performance trends</li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Sectors We Serve</h2>
            <ul className="benefits-list" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <li style={{ fontSize: '0.95rem', padding: '0.75rem' }}><i className="fas fa-industry"></i> Manufacturing & Textiles</li>
              <li style={{ fontSize: '0.95rem', padding: '0.75rem' }}><i className="fas fa-shopping-cart"></i> FMCG & Beverages</li>
              <li style={{ fontSize: '0.95rem', padding: '0.75rem' }}><i className="fas fa-truck"></i> Logistics & Supply Chain</li>
              <li style={{ fontSize: '0.95rem', padding: '0.75rem' }}><i className="fas fa-laptop-code"></i> Tech & Startups</li>
              <li style={{ fontSize: '0.95rem', padding: '0.75rem' }}><i className="fas fa-hard-hat"></i> Construction & Engineering</li>
              <li style={{ fontSize: '0.95rem', padding: '0.75rem' }}><i className="fas fa-store"></i> Retail & Services</li>
              <li style={{ fontSize: '0.95rem', padding: '0.75rem' }}><i className="fas fa-leaf"></i> NGOs & Development Sector</li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Payroll Processed Impact</h2>
            <ul className="benefits-list">
              <li>
                <span className="benefit-heading">35% Reduction in Administrative Time</span>
              </li>
              <li>
                <span className="benefit-heading">99.9% Accuracy in Monthly Salary Disbursement</span>
              </li>
              <li>
                <span className="benefit-heading">100% Compliance with Labor Laws and Audits</span>
              </li>
              <li>
                <span className="benefit-heading">Enhanced Employee Trust and Satisfaction</span>
                <span className="benefit-subheading">through transparency</span>
              </li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Analysis Summary</h2>
            <p style={{ textAlign: 'center', fontSize: '1.1rem', lineHeight: '1.8', maxWidth: '900px', margin: '0 auto', fontWeight: '500' }}>
              With over <strong>5,000+ employees managed monthly</strong> and <strong>50+ satisfied corporate clients</strong>, HRCONSOL's Payroll Management System has redefined how businesses handle compensation. Our automated, transparent, and scalable solution enables organizations to achieve efficiency, reduce costs, and build a compliant workforce ecosystem.
            </p>
          </div>

          <div className="service-section">
            <h2>Our Payroll Management Process</h2>
            <p style={{ textAlign: 'center', fontSize: '1.05rem', marginBottom: '2rem', color: '#666' }}>
              At HRCONSOL, we follow a structured, transparent, and automated payroll process to ensure accuracy, compliance, and timeliness across all levels of your organization.
            </p>
            <div className="process-steps">
              <div className="step">
                <h3><i className="fas fa-database"></i> Data Collection & Validation</h3>
                <p>Gathering and verifying employee information and attendance data.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-money-bill-wave"></i> Salary Structuring</h3>
                <p>Configuring compensation packages and employee grades.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-calculator"></i> Payroll Calculation</h3>
                <p>Automated processing of salaries, deductions, and benefits.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-balance-scale"></i> Statutory Compliance</h3>
                <p>Ensuring deductions for EOBI, tax, and other statutory requirements.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-check-circle"></i> Approval & Audit Trail</h3>
                <p>Management review and documented approval process.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-university"></i> Salary Disbursement</h3>
                <p>Direct bank transfers to employee accounts.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-file-alt"></i> Payslip & Self-Service</h3>
                <p>Digital payslips accessible through employee portal.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-chart-line"></i> Reporting & Analytics</h3>
                <p>Comprehensive insights and compliance reporting.</p>
              </div>
            </div>
          </div>

          <div className="service-cta">
            <h2>Ready to Simplify Your Payroll?</h2>
            <p>Let HRCONSOL handle your payroll while you focus on growing your business.</p>
            <button onClick={() => navigate('/#contact')} className="cta-button">Contact Us</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollOutsourcing;

